import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import mixpanel from 'mixpanel-browser';

const MIXPANEL_TOKEN = '1ff0d86d023338262f9ffc9d24808b4c';

// Use a module-level variable to act as a guard.
// This will persist across the component's mount/unmount/remount cycle in Strict Mode.
let mixpanelInitialized = false;

interface MixpanelContextType {
  isInitialized: boolean;
}

const MixpanelContext = createContext<MixpanelContextType>({
  isInitialized: false,
});

export const useMixpanel = () => useContext(MixpanelContext);

export const MixpanelProvider = ({ children }: { children: ReactNode }) => {
  const [isInitialized, setIsInitialized] = useState(mixpanelInitialized);

  useEffect(() => {
    // If Mixpanel has already been initialized (either in a previous render or
    // by another instance of the provider), don't do it again.
    if (mixpanelInitialized) {
      return;
    }

    const isDev = import.meta.env.DEV;

    const loadedCallback = () => {
      setIsInitialized(true);
      mixpanelInitialized = true; // Set the guard after successful initialization
      if (isDev) {
        console.log('[Mixpanel] Initialized successfully.');
      }
    };

    if (isDev) {
      console.log('[Mixpanel] Initializing in development (debug) mode.');
      mixpanel.init(MIXPANEL_TOKEN, {
        debug: false,
        autocapture: true,
        record_sessions_percent: 100,
        loaded: loadedCallback
      });
    } else {
      mixpanel.init(MIXPANEL_TOKEN, {
        autocapture: true,
        record_sessions_percent: 100,
        loaded: loadedCallback
      });
    }
  }, []); // Empty dependency array ensures this runs only once on mount.

  return (
    <MixpanelContext.Provider value={{ isInitialized }}>
      {children}
    </MixpanelContext.Provider>
  );
};